import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AppLogo from "../components/AppLogo";

import { useAuth, roleHome } from "../lib/auth";
import { apiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const user = await login(email, password);
      const destination =
        location.state?.from?.pathname || roleHome(user.role);

      navigate(destination, { replace: true });
      toast.success(`Welcome back, ${user.full_name}`);
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-mb-bg text-white">
      <header className="border-b border-mb-border px-6 py-4 lg:px-12">
        <AppLogo
          to="/"
          subtitle="Smart Clinic • Easy Care"
          size={48}
        />
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md" data-testid="login-card">
          <div className="mb-8">
            <div className="mb-5 flex justify-center sm:hidden">
              <AppLogo
                to="/"
                subtitle="Smart Clinic • Easy Care"
                size={56}
              />
            </div>

            <h1 className="text-3xl font-light tracking-tight">
              Sign in
            </h1>

            <p className="mt-2 text-sm text-mb-fg2">
              Access your MediBooks account securely.
            </p>

            <p className="mt-1 text-xs text-mb-fg2">
              For hospital admins, doctors, patients, and super admins.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs uppercase tracking-[0.2em] text-mb-fg2"
              >
                Email
              </Label>

              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                data-testid="login-email"
                placeholder="you@hospital.com"
                className="h-11 rounded-lg border-mb-border bg-mb-surface2 text-white focus-visible:ring-teal-500"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs uppercase tracking-[0.2em] text-mb-fg2"
              >
                Password
              </Label>

              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                data-testid="login-password"
                className="h-11 rounded-lg border-mb-border bg-mb-surface2 text-white focus-visible:ring-teal-500"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="h-11 w-full rounded-lg bg-teal-500 font-medium text-zinc-950 hover:bg-teal-400"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 space-y-2 border-t border-mb-border pt-6 text-sm text-mb-fg2">
            <div>
              New patient?{" "}
              <Link
                to="/signup/patient"
                className="text-teal-400 hover:text-teal-300"
                data-testid="link-signup-patient"
              >
                Create an account
              </Link>
            </div>

            <div>
              Run a clinic?{" "}
              <Link
                to="/signup/hospital"
                className="text-teal-400 hover:text-teal-300"
                data-testid="link-signup-hospital"
              >
                Register your hospital
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-mb-border px-6 py-5 text-center text-xs text-mb-fg2">
        <div>© 2026 MediBooks</div>
        <div className="mt-1">
          A Product of MNV Solutions
        </div>
      </footer>
    </div>
  );
}